import React from "react";
import { FalkeCoinLogo } from "../../components/Brand";
import { Award, Sparkles, TrendingUp, Trophy, Coins, Gift } from "lucide-react";

const STUFEN = [
  { name: "Bronze", schwelle: 1, color: "from-amber-700 to-amber-500", text: "text-amber-900", ring: "ring-amber-300" },
  { name: "Silber", schwelle: 3, color: "from-slate-400 to-slate-300", text: "text-slate-900", ring: "ring-slate-300" },
  { name: "Gold", schwelle: 5, color: "from-yellow-500 to-yellow-300", text: "text-yellow-900", ring: "ring-yellow-300" },
  { name: "Platin", schwelle: 10, color: "from-indigo-300 to-cyan-200", text: "text-indigo-900", ring: "ring-cyan-300" },
];

const QUARTAL = [
  { range: "1.–2. Empfehlung", coins: "200 FC", bonus: "Standard", color: "bg-falke-blue/5 border-falke-blue/20" },
  { range: "3.–4. Empfehlung", coins: "250 FC", bonus: "+50 FC Bonus", color: "bg-falke-orange/10 border-falke-orange/30" },
  { range: "Ab 5. Empfehlung", coins: "300 FC", bonus: "+100 FC Bonus", color: "bg-falke-gold/20 border-falke-gold/40" },
];

export default function ProgrammRegeln() {
  return (
    <div className="space-y-8 max-w-4xl" data-testid="programm-regeln-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#2D5C7E] to-[#1C425E] text-white p-8 sm:p-10">
        <div className="absolute -top-8 -right-8 opacity-20 pointer-events-none">
          <FalkeCoinLogo size={180} />
        </div>
        <div className="relative z-10 max-w-xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-falke-orange/25 backdrop-blur text-falke-gold text-xs font-semibold uppercase tracking-wider mb-4">
            Wie funktioniert&apos;s?
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-3 leading-tight">
            Empfehlen.<br/>Belohnt werden.
          </h1>
          <p className="text-white/85 leading-relaxed">
            Jede erfolgreiche Empfehlung schenkt dir FalkeCoins. Sie steigern deine Treuestufe, schalten Premium-Geschenke frei
            und bringen extra Bonus bei aktivem Empfehlen. Hier siehst du alle Regeln auf einen Blick.
          </p>
        </div>
      </div>

      {/* Vergütung */}
      <section className="falke-card p-6 sm:p-8" data-testid="regeln-verguetung">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-2xl bg-falke-orange/15 flex items-center justify-center">
            <Coins className="text-falke-orange" size={22} />
          </div>
          <div>
            <h2 className="font-heading text-xl text-falke-blue">Standard-Vergütung</h2>
            <p className="text-sm text-falke-anthracite/70">Pro erfolgreich abgeschlossener Empfehlung</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] items-center gap-4 rounded-2xl border-2 border-dashed border-falke-orange/40 bg-falke-orange/5 p-5">
          <div>
            <div className="font-semibold text-falke-blue">200 FalkeCoins</div>
            <div className="text-sm text-falke-anthracite/70">= 200 € Gegenwert · einlösbar in Geschenken oder Shop-Produkten</div>
          </div>
          <div className="text-4xl font-heading font-bold text-falke-orange">200 FC</div>
        </div>
      </section>

      {/* Stufen */}
      <section className="falke-card p-6 sm:p-8" data-testid="regeln-stufen">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-2xl bg-falke-blue/10 flex items-center justify-center">
            <Trophy className="text-falke-blue" size={22} />
          </div>
          <div>
            <h2 className="font-heading text-xl text-falke-blue">Treuestufen</h2>
            <p className="text-sm text-falke-anthracite/70">
              <span className="font-semibold text-falke-blue">Lebenslang gültig.</span> Einmal erreicht – nie wieder verloren.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
          {STUFEN.map((s, i) => (
            <div key={s.name} className="relative rounded-2xl border border-falke-blue/10 p-5 bg-white hover:shadow-[0_12px_32px_rgba(45,92,126,0.08)] transition" data-testid={`stufe-card-${s.name.toLowerCase()}`}>
              <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${s.color} ring-4 ${s.ring} flex items-center justify-center mb-3`}>
                <Award className={s.text} size={26} />
              </div>
              <div className="font-heading text-lg text-falke-blue">{s.name}</div>
              <div className="text-sm text-falke-anthracite/70 mt-1">
                ab <span className="font-semibold text-falke-blue">{s.schwelle}</span> erfolgreichen Empfehlung{s.schwelle === 1 ? "" : "en"}
              </div>
              {i === 3 && (
                <div className="absolute -top-2 -right-2 px-2.5 py-0.5 rounded-full bg-falke-gold text-falke-anthracite text-[10px] font-bold tracking-wider uppercase shadow">
                  Premium
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Quartals-Bonus */}
      <section className="falke-card p-6 sm:p-8" data-testid="regeln-quartal">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-falke-orange/15 flex items-center justify-center">
            <TrendingUp className="text-falke-orange" size={22} />
          </div>
          <div>
            <h2 className="font-heading text-xl text-falke-blue">Quartals-Bonus</h2>
            <p className="text-sm text-falke-anthracite/70">Je mehr du im laufenden Quartal empfiehlst, desto mehr Coins bekommst du pro Empfehlung.</p>
          </div>
        </div>

        <div className="space-y-3">
          {QUARTAL.map((q) => (
            <div key={q.range} className={`flex items-center justify-between gap-3 rounded-2xl border ${q.color} p-4`} data-testid={`quartal-${q.coins}`}>
              <div>
                <div className="font-semibold text-falke-blue">{q.range}</div>
                <div className="text-xs text-falke-anthracite/70 mt-0.5">{q.bonus}</div>
              </div>
              <div className="text-2xl font-heading font-bold text-falke-orange">{q.coins}</div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-start gap-3 rounded-2xl bg-gradient-to-r from-falke-gold/30 to-falke-orange/20 border border-falke-gold/40 p-4" data-testid="quartal-spezialbonus">
          <Sparkles className="text-falke-orange flex-shrink-0 mt-0.5" size={22} />
          <div>
            <div className="font-heading text-falke-blue">Spezial-Bonus</div>
            <div className="text-sm text-falke-anthracite/80">
              Schaffst du <span className="font-semibold text-falke-blue">5 oder mehr Empfehlungen im selben Quartal</span>, gibt&apos;s
              einmalig <span className="font-bold text-falke-orange">+250 FC</span> extra obendrauf. Quasi ein &bdquo;Aktiv-Bonus&ldquo; für deinen Einsatz.
            </div>
          </div>
        </div>
      </section>

      {/* Einlösen */}
      <section className="falke-card p-6 sm:p-8" data-testid="regeln-einloesen">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-falke-blue/10 flex items-center justify-center">
            <Gift className="text-falke-blue" size={22} />
          </div>
          <div>
            <h2 className="font-heading text-xl text-falke-blue">Was du mit deinen FalkeCoins machst</h2>
            <p className="text-sm text-falke-anthracite/70">1 FalkeCoin = 1 € · Sammeln, behalten, einlösen — du entscheidest, wann.</p>
          </div>
        </div>
        <ul className="grid sm:grid-cols-2 gap-3 mt-2 text-sm">
          <li className="flex items-start gap-3"><span className="w-2 h-2 mt-2 rounded-full bg-falke-orange flex-shrink-0" /><span>Premium-Geschenke (iPad, E-Bike, Wellness, …) direkt im Trophäen-Regal einlösen</span></li>
          <li className="flex items-start gap-3"><span className="w-2 h-2 mt-2 rounded-full bg-falke-orange flex-shrink-0" /><span>Produkte im FalkeTek-Shop kaufen oder Coins als Rabatt nutzen</span></li>
          <li className="flex items-start gap-3"><span className="w-2 h-2 mt-2 rounded-full bg-falke-orange flex-shrink-0" /><span>Höhere Treuestufen schalten exklusive Geschenke frei</span></li>
          <li className="flex items-start gap-3"><span className="w-2 h-2 mt-2 rounded-full bg-falke-orange flex-shrink-0" /><span>Empfiehlst du regelmäßig? Werde gewerblicher Tippgeber – mit 2 % Geld-Provision</span></li>
        </ul>
      </section>

      <p className="text-xs text-falke-anthracite/50 text-center px-4">
        Coins werden nach 7-tägiger Sperrfrist gutgeschrieben (nach Vollzahlung der Empfehlung). Stufen werden lebenslang gehalten.
      </p>
    </div>
  );
}
